import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Receipt, Star, Loader2, Printer, Ban } from "lucide-react";
import Layout from "@/components/Layout";
import PayButton from "@/components/PayButton";
import { StatusBadge } from "@/components/Quote";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";

function ReviewForm({ bookingId, onDone }) {
  const [rating, setRating] = useState(5); const [text, setText] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.length < 5) { toast.error("Write a few words"); return; }
    setBusy(true);
    try { await api.post("/reviews", { booking_id: bookingId, rating, text }); toast.success("Review submitted for moderation"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid="review-form">
      <h3 className="font-display font-bold">Rate your experience</h3>
      <div className="flex gap-1">{[1,2,3,4,5].map((n) => <button key={n} data-testid={`rating-star-${n}`} onClick={() => setRating(n)}><Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}</div>
      <Textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="How was the vendor?" className="rounded-xl" />
      <Button data-testid="review-submit-btn" onClick={submit} disabled={busy} className="rounded-xl bg-gradient-to-r from-purple-600 to-pink-500">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Review"}</Button>
    </div>
  );
}

export function Invoice({ bookingId }) {
  const [inv, setInv] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/invoice`).then((r) => setInv(r.data));
  }, [bookingId]);

  if (!inv) {
    return <Loader2 className="h-6 w-6 animate-spin mx-auto my-10" />;
  }

  const b = inv.booking || {};
  const vendor = inv.vendor || {};
  const customer = inv.customer || {};
  const platform = inv.platform || {};
  const payments = inv.payments || [];

  const vendorLogo =
    vendor.logo_url ||
    vendor.logo ||
    vendor.logoUrl ||
    "";

  const vendorName = vendor.business_name || "Vendor";
  const customerName = customer.name || "Customer";

  const eventType = b.event_type || b.event || "Event";
  const eventDate = b.event_date
    ? new Date(b.event_date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

  const eventTime = b.event_time || "";
  const location = b.location || "—";
  const guests = b.guests ? `${b.guests} Guests` : "";

  const standardTerms = [
    "To confirm the booking, the required advance payment must be paid. Upon receipt, the vendor will block the event date.",
    "If the event is cancelled by the customer, the advance amount will not be refunded unless otherwise agreed in writing.",
    "Any additional duration beyond the agreed event/service time may attract additional charges.",
    "Any additional services, items, equipment, travel, or requirements requested after confirmation will be charged separately.",
    "The balance payment must be settled according to the payment schedule mutually agreed between the customer and vendor.",
  ];

  const customTerms =
    b.terms ||
    b.notes ||
    b.cancellation_policy ||
    "";

  const printInvoice = () => {
    document.body.classList.add("invoice-print-mode");

    const cleanup = () => {
      document.body.classList.remove("invoice-print-mode");
      window.removeEventListener("afterprint", cleanup);
    };

    window.addEventListener("afterprint", cleanup);
    window.print();

    // Fallback for browsers where afterprint is delayed/not fired.
    setTimeout(() => {
      document.body.classList.remove("invoice-print-mode");
    }, 60000);
  };

  return (
    <>
      <style>{`
        @page {
          size: A4;
          margin: 0;
        }

        .invoice-print-root {
          width: 100%;
          max-width: 210mm;
          margin: 0 auto;
          background: #ffffff;
          color: #172033;
          font-family: Arial, Helvetica, sans-serif;
        }

        .invoice-paper {
          width: 100%;
          min-height: 270mm;
          box-sizing: border-box;
          padding: 12mm 13mm 10mm;
          background: #ffffff;
        }

        .invoice-gradient {
          background: linear-gradient(135deg, #5e17eb 0%, #cc00ff 100%);
        }

        .invoice-table {
          width: 100%;
          border-collapse: collapse;
        }

        .invoice-table th,
        .invoice-table td {
          padding: 8px 7px;
          border-bottom: 1px solid #e8eaf0;
        }

        .invoice-table th {
          background: #f6f5fb;
          color: #4b5563;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .05em;
        }

        .invoice-table td {
          font-size: 11px;
        }

        .invoice-small {
          font-size: 9px;
          line-height: 1.45;
        }

        .invoice-muted {
          color: #697386;
        }

        .invoice-print-mode body {
          background: #ffffff !important;
        }

        @media print {
          @page {
            size: A4;
            margin: 0;
          }

          html,
          body {
            width: 100% !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #ffffff !important;
          }

          body.invoice-print-mode > * {
            display: none !important;
          }

          body.invoice-print-mode .invoice-print-root {
            display: block !important;
            position: static !important;
            width: 210mm !important;
            max-width: 210mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
            background: #ffffff !important;
            color: #172033 !important;
          }

          body.invoice-print-mode .invoice-print-root,
          body.invoice-print-mode .invoice-print-root * {
            visibility: visible !important;
          }

          body.invoice-print-mode .invoice-paper {
            width: 210mm !important;
            min-height: 0 !important;
            height: auto !important;
            margin: 0 !important;
            padding: 12mm 13mm 10mm !important;
            box-sizing: border-box !important;
            background: #ffffff !important;
          }

          body.invoice-print-mode .invoice-print-button {
            display: none !important;
          }

          body.invoice-print-mode .invoice-avoid-break,
          body.invoice-print-mode .invoice-table,
          body.invoice-print-mode .invoice-footer {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          body.invoice-print-mode .invoice-table tr {
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      <div
        className="invoice-print-root"
        data-testid="invoice"
      >
        <div className="invoice-paper">

          {/* Header */}
          <div className="invoice-avoid-break">
            <div className="flex items-start justify-between gap-6 pb-5 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl invoice-gradient flex items-center justify-center overflow-hidden shadow-sm">
                  {vendorLogo ? (
                    <img
                      src={vendorLogo}
                      alt={vendorName}
                      className="h-full w-full object-contain bg-white"
                    />
                  ) : (
                    <span className="text-white text-lg font-extrabold">
                      {vendorName.substring(0, 1).toUpperCase()}
                    </span>
                  )}
                </div>

                <div>
                  <div className="text-lg font-extrabold tracking-tight text-slate-900">
                    {vendorName}
                  </div>
                  <div className="text-[10px] invoice-muted">
                    {vendor.address || "Event & Professional Services"}
                  </div>
                  {vendor.phone && (
                    <div className="text-[10px] invoice-muted">
                      {vendor.phone}
                    </div>
                  )}
                  {vendor.gst_number && (
                    <div className="text-[10px] font-semibold text-slate-600">
                      GSTIN: {vendor.gst_number}
                    </div>
                  )}
                </div>
              </div>

              <div className="text-right">
                <div className="text-[10px] font-bold tracking-[0.18em] text-purple-600 uppercase">
                  Invoice
                </div>
                <div className="text-xl font-extrabold text-slate-900 mt-1">
                  {inv.invoice_no}
                </div>
                <div className="text-[10px] invoice-muted mt-1">
                  Issued:{" "}
                  {inv.issued_at
                    ? new Date(inv.issued_at).toLocaleDateString("en-IN")
                    : "—"}
                </div>
              </div>
            </div>

            {/* Event / Customer information */}
            <div className="grid grid-cols-2 gap-4 mt-5">
              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-purple-600 mb-2">
                  Bill To
                </div>
                <div className="font-bold text-sm text-slate-900">
                  {customerName}
                </div>
                {customer.phone && (
                  <div className="text-[10px] invoice-muted mt-1">
                    +91 {customer.phone}
                  </div>
                )}
                {customer.email && (
                  <div className="text-[10px] invoice-muted">
                    {customer.email}
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-purple-600 mb-2">
                  Event Details
                </div>
                <div className="font-bold text-sm text-slate-900">
                  {eventType}
                </div>
                <div className="text-[10px] invoice-muted mt-1">
                  {eventDate}
                  {eventTime ? ` · ${eventTime}` : ""}
                </div>
                <div className="text-[10px] invoice-muted">
                  {location}
                  {guests ? ` · ${guests}` : ""}
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="mt-6 invoice-avoid-break">
            <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-slate-500 mb-2">
              Services & Items
            </div>

            <table className="invoice-table">
              <thead>
                <tr>
                  <th className="text-left">Description</th>
                  <th className="text-right">Qty</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(b.items || []).map((item, index) => (
                  <tr key={index}>
                    <td className="font-medium text-slate-800">
                      {item.name || "Service"}
                    </td>
                    <td className="text-right text-slate-600">
                      {item.qty}
                    </td>
                    <td className="text-right text-slate-600">
                      {formatINR(item.unit_price)}
                    </td>
                    <td className="text-right font-semibold text-slate-900">
                      {formatINR(item.qty * item.unit_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-5 invoice-avoid-break">
            <div className="w-[82mm]">
              <div className="flex justify-between py-1.5 text-[11px] text-slate-600">
                <span>Subtotal</span>
                <span>{formatINR(b.subtotal || 0)}</span>
              </div>

              {b.discount > 0 && (
                <div className="flex justify-between py-1.5 text-[11px] text-slate-600">
                  <span>Discount</span>
                  <span>-{formatINR(b.discount)}</span>
                </div>
              )}

              {b.tax > 0 && (
                <div className="flex justify-between py-1.5 text-[11px] text-slate-600">
                  <span>Tax / GST</span>
                  <span>{formatINR(b.tax)}</span>
                </div>
              )}

              <div className="invoice-gradient text-white rounded-xl px-4 py-3 mt-2 flex justify-between items-center">
                <span className="text-xs font-semibold">Grand Total</span>
                <span className="text-lg font-extrabold">
                  {formatINR(b.total || 0)}
                </span>
              </div>

              <div className="flex justify-between py-2 text-[11px] text-emerald-700">
                <span>Amount Paid</span>
                <span className="font-bold">{formatINR(inv.paid || 0)}</span>
              </div>

              <div className="flex justify-between py-2 text-[12px] font-bold text-amber-700 border-t border-slate-200">
                <span>Balance Due</span>
                <span>{formatINR(inv.due || 0)}</span>
              </div>
            </div>
          </div>

          {/* Payment details */}
          <div className="grid grid-cols-2 gap-4 mt-5 invoice-avoid-break">
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-purple-600 mb-2">
                Payment Details
              </div>

              {payments.length > 0 ? (
                <div className="space-y-1.5">
                  {payments.map((payment, index) => (
                    <div key={payment.id || index} className="text-[9px] text-slate-600">
                      <span className="font-semibold capitalize">
                        {payment.type || "Payment"}
                      </span>
                      {" · "}
                      {formatINR(payment.amount)}
                      {" · "}
                      {payment.provider || "—"}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[9px] invoice-muted">
                  No payment recorded yet.
                </div>
              )}

              {vendor.bank?.upi_id && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-[9px] text-slate-500">UPI ID</div>
                  <div className="text-[10px] font-bold text-slate-800">
                    {vendor.bank.upi_id}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-purple-600 mb-2">
                Booking Status
              </div>
              <div className="text-sm font-bold capitalize text-slate-900">
                {(b.status || "confirmed").replace(/_/g, " ")}
              </div>
              {b.cancellation_policy && (
                <div className="mt-2 text-[9px] leading-4 text-slate-500">
                  <span className="font-semibold text-slate-700">
                    Cancellation:
                  </span>{" "}
                  {b.cancellation_policy}
                </div>
              )}
            </div>
          </div>

          {/* Terms */}
          <div className="mt-5 invoice-avoid-break">
            <div className="text-[9px] font-bold tracking-[0.12em] uppercase text-purple-600 mb-2">
              Terms & Conditions
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
              <ol className="list-decimal pl-4 space-y-1">
                {standardTerms.map((term, index) => (
                  <li key={index} className="invoice-small text-slate-600">
                    {term}
                  </li>
                ))}
              </ol>

              {customTerms && (
                <div className="mt-3 pt-3 border-t border-slate-200 invoice-small text-slate-600">
                  <span className="font-bold text-slate-700">
                    Additional Terms:
                  </span>{" "}
                  {customTerms}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="invoice-footer mt-7 pt-5 border-t border-slate-200 text-center">
            <div className="text-sm font-bold text-slate-800">
              Thank you for choosing @ {vendorName}.
            </div>

            <div className="text-xs font-semibold text-purple-600 mt-1">
              Thank you for choosing MakeMyEventPro.com
            </div>

            <div className="mt-4 text-[9px] tracking-wide text-slate-500">
              MakeMyEventPro.com
            </div>

            <div className="text-[9px] font-bold tracking-[0.12em] text-slate-700 uppercase mt-1">
              Powered by EXACT MEDIA
            </div>

            {platform.email && (
              <div className="text-[8px] text-slate-400 mt-2">
                {platform.email}
              </div>
            )}
          </div>

          {/* Screen-only print button */}
          <div className="invoice-print-button flex justify-end mt-5">
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={printInvoice}
            >
              <Printer className="h-4 w-4 mr-1" />
              Print / Save PDF
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function BookingDetail() {
  const { id } = useParams(); const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [d, setD] = useState(null); const [inv, setInv] = useState(false); const [cancelOpen, setCancelOpen] = useState(false); const [note, setNote] = useState("");
  const load = useCallback(() => api.get(`/bookings/${id}`).then((r) => setD(r.data)).catch(() => setD(false)), [id]);
  useEffect(() => { if (!user) { setAuthOpen(true); return; } load(); }, [user, load, setAuthOpen]);
  if (d === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (d === false) return <Layout><div className="py-20 text-center text-muted-foreground">Booking not found.</div></Layout>;
  const b = d.booking; const due = b.total - b.paid_amount;
  const canPay = ["payment_pending", "confirmed", "in_progress"].includes(b.status) && due > 0;
  const canCancel = ["payment_pending", "confirmed"].includes(b.status);
  const cancel = async () => {
    try { const { data } = await api.post(`/bookings/${b.id}/status`, { status: "cancelled", note }); toast.success(data.status === "refund_requested" ? "Cancellation requested — refund under review" : "Booking cancelled"); setCancelOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/bookings")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ChevronLeft className="h-4 w-4" /> My Bookings</button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><h1 className="font-display font-extrabold text-2xl">{b.vendor_name}</h1><p className="text-muted-foreground text-sm">{b.code} · {b.event_type} · {b.event_date || "TBD"} · {b.location} {b.guests ? `· ${b.guests} guests` : ""}</p></div>
          <StatusBadge status={b.status} />
        </div>
        <div className="grid lg:grid-cols-[1fr_380px] gap-6 mt-6">
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-display font-bold mb-3">Order summary</h3>
              {b.items.map((i, k) => <div key={k} className="flex justify-between text-sm py-1 border-b border-border/50"><span>{i.name} × {i.qty}</span><span>{formatINR(i.qty * i.unit_price)}</span></div>)}
              <div className="flex justify-between font-display font-extrabold text-lg mt-3"><span>Total</span><span data-testid="booking-total">{formatINR(b.total)}</span></div>
              <div className="flex justify-between text-sm text-emerald-600"><span>Paid</span><span data-testid="booking-paid">{formatINR(b.paid_amount)}</span></div>
              <div className="flex justify-between text-sm text-amber-600"><span>Balance due</span><span>{formatINR(due)}</span></div>
              {b.cancellation_policy && <p className="text-xs text-muted-foreground mt-3"><b>Cancellation policy:</b> {b.cancellation_policy}</p>}
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-display font-bold mb-3">Timeline</h3>
              <ol className="space-y-2" data-testid="booking-history">{(b.history || []).map((h, i) => <li key={i} className="flex gap-3 text-sm"><span className="h-2 w-2 mt-1.5 rounded-full bg-purple-500 shrink-0" /><div><span className="font-medium capitalize">{h.status.replace(/_/g, " ")}</span> <span className="text-muted-foreground">· {h.by} · {new Date(h.at).toLocaleString("en-IN")}</span>{h.note && <div className="text-muted-foreground">{h.note}</div>}</div></li>)}</ol>
            </div>
            {d.payments.length > 0 && <div className="rounded-2xl border border-border bg-card p-5"><h3 className="font-display font-bold mb-3">Payments</h3>{d.payments.map((p) => <div key={p.id} className="flex justify-between text-sm py-1"><span className="capitalize">{p.type} · {p.provider}</span><span className="flex items-center gap-2">{formatINR(p.amount)} <StatusBadge status={p.status} /></span></div>)}</div>}
            {b.status === "completed" && !d.review && <ReviewForm bookingId={b.id} onDone={load} />}
            {d.review && <div className="rounded-2xl border border-border bg-card p-5 text-sm"><b>Your review</b> · {d.review.rating}★ · <StatusBadge status={d.review.status} /><p className="mt-1 text-muted-foreground">{d.review.text}</p></div>}
          </div>
          <div className="space-y-4">
            {canPay && (d.payment_config?.checkout_available !== false
              ? <div className="rounded-2xl border border-border bg-card p-5"><h3 className="font-display font-bold mb-3">{b.paid_amount === 0 ? "Pay advance to confirm" : "Pay balance"}</h3><PayButton booking={b} type={b.paid_amount === 0 ? "advance" : "balance"} config={d.payment_config} demoEnabled={d.demo_payment_enabled} onPaid={load} /></div>
              : <div data-testid="payment-unavailable" className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800">Online payment is temporarily unavailable. Please contact the vendor or our support team to complete your booking.</div>)}
            <div className="rounded-2xl border border-border bg-card p-5 space-y-2">
              <Button data-testid="invoice-btn" variant="outline" className="w-full rounded-xl" onClick={() => setInv(true)}><Receipt className="h-4 w-4 mr-2" /> Invoice / Receipt</Button>
              {canCancel && <Button data-testid="cancel-booking-btn" variant="outline" className="w-full rounded-xl text-red-500 border-red-200" onClick={() => setCancelOpen(true)}><Ban className="h-4 w-4 mr-2" /> Cancel booking</Button>}
              <Button variant="ghost" className="w-full rounded-xl" onClick={() => navigate(`/enquiries/${b.enquiry_id}`)}>Open chat with vendor</Button>
            </div>
          </div>
        </div>
      </div>
      <Dialog open={inv} onOpenChange={setInv}>
        <DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-4xl w-[96vw] max-h-[92vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-5 pb-3 bg-white dark:bg-slate-900 border-b shrink-0">
            <DialogTitle>Invoice / Receipt</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pb-8 overscroll-contain">
            <Invoice bookingId={b.id} />
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}><DialogContent className="bg-white dark:bg-slate-900 rounded-3xl max-w-md"><DialogHeader><DialogTitle>Cancel booking?</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{b.cancellation_policy}</p>{b.paid_amount > 0 && <p className="text-sm">You have paid {formatINR(b.paid_amount)}. A refund request will be raised per the policy.</p>}
        <Textarea data-testid="cancel-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" className="rounded-xl" />
        <Button data-testid="cancel-confirm-btn" onClick={cancel} className="rounded-xl bg-red-500 hover:bg-red-600">Confirm cancellation</Button></DialogContent></Dialog>
    </Layout>
  );
}
